import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { X, ChevronLeft, ChevronRight, Check, Upload, Building, Briefcase, User, Globe, Phone, Mail, Image as ImageIcon, FileText, Shield, Send } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import '../styles/BusinessBookingModal.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const TOTAL_STEPS = 8;

const ZONE_META = {
  freezone: { name: 'Free Zone Company', basePrice: 20000, visaPrice: 4000, icon: Briefcase, includesVisa: false },
  mainland: { name: 'Mainland Company', basePrice: 16000, visaPrice: 4000, icon: Building, includesVisa: false },
};

// Parse a DB price string like "AED 20,000" into a Number.
// Returns null for invalid/empty input so callers can decide the fallback.
const parsePriceToNumber = (priceStr) => {
  if (priceStr === null || priceStr === undefined) return null;
  const digits = String(priceStr).replace(/[^\d.]/g, '');
  if (!digits) return null;
  const n = Number(digits);
  return Number.isFinite(n) && n > 0 ? n : null;
};

const formatDate = (d) => {
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

const STEP_META = [
  { num: 1, title: 'Company Name', Icon: Building },
  { num: 2, title: 'Your Name', Icon: User },
  { num: 3, title: 'Nationality', Icon: Globe },
  { num: 4, title: 'Phone & Telegram', Icon: Phone },
  { num: 5, title: 'Email Address', Icon: Mail },
  { num: 6, title: 'Personal Photo', Icon: ImageIcon },
  { num: 7, title: 'Passport Copy', Icon: FileText },
  { num: 8, title: 'Review & Submit', Icon: Check },
];

const BusinessBookingModal = ({ isOpen, onClose, zone = 'freezone', service = null }) => {
  const { t } = useLanguage();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [telegramDeepLink, setTelegramDeepLink] = useState('');
  const [error, setError] = useState('');
  const photoRef = useRef(null);
  const passportRef = useRef(null);

  const meta = ZONE_META[zone] || ZONE_META.freezone;

  // Prefer the admin-managed DB price (via the `service` prop) over the
  // hardcoded ZONE_META fallback so price edits in Admin Panel flow through.
  const dbBasePrice = parsePriceToNumber(service?.price);
  const basePrice = dbBasePrice !== null ? dbBasePrice : meta.basePrice;
  const visaAddOnPrice = meta.visaPrice; // only applied for zones where visa is extra (mainland)

  const [form, setForm] = useState({
    companyName: '',
    firstName: '',
    lastName: '',
    nationality: '',
    mobile: '',
    telegram: '',
    sameAsPhone: false, // default OFF — show both fields so client can fill them; can check to reuse phone
    email: '',
    photo: null,
    photoPreview: null,
    passport: null,
    passportPreview: null,
    wantVisa: meta.includesVisa,
  });

  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setSuccess(false);
      setError('');
      setTelegramDeepLink('');
      setForm((prev) => ({ ...prev, wantVisa: meta.includesVisa }));
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen, meta.includesVisa]);

  if (!isOpen) return null;

  // Visa is always a paid add-on of `visaAddOnPrice` for both zones.
  // Base price comes from the admin-managed DB record (service.price).
  const totalPrice = basePrice + (form.wantVisa ? visaAddOnPrice : 0);

  // Investor visa preview dates — issued today, valid for 2 years.
  const issueDate = new Date();
  const expiryDate = new Date();
  expiryDate.setFullYear(expiryDate.getFullYear() + 2);

  const update = (key, value) => setForm((p) => ({ ...p, [key]: value }));

  const handleFile = (key, previewKey) => (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setForm((p) => ({ ...p, [key]: file, [previewKey]: ev.target.result }));
    };
    reader.readAsDataURL(file);
  };

  const canProceed = () => {
    switch (step) {
      case 1: return form.companyName.trim().length >= 2;
      case 2: return form.firstName.trim() && form.lastName.trim();
      case 3: return form.nationality.trim().length >= 2;
      case 4:
        if (form.mobile.trim().length < 4) return false;
        if (!form.sameAsPhone && form.telegram.trim().length < 3) return false;
        return true;
      case 5: return form.email.includes('@') && form.email.includes('.');
      case 6: return true; // photo optional
      case 7: return true; // passport optional
      case 8: return true;
      default: return false;
    }
  };

  const next = () => step < TOTAL_STEPS && setStep(step + 1);
  const back = () => step > 1 && setStep(step - 1);

  const submit = async () => {
    setSubmitting(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('companyName', form.companyName);
      fd.append('firstName', form.firstName);
      fd.append('lastName', form.lastName);
      fd.append('nationality', form.nationality);
      fd.append('mobile', form.mobile);
      fd.append('email', form.email);
      // Telegram value: same as phone if checkbox is checked, else explicit field.
      const telegramValue = form.sameAsPhone
        ? (form.mobile || '').trim()
        : (form.telegram || '').trim();
      const contactValue = telegramValue;
      if (telegramValue) {
        fd.append('telegram', telegramValue);
      }
      fd.append('includesVisa', String(form.wantVisa));
      fd.append('zone', zone);
      fd.append('totalPrice', `AED ${totalPrice.toLocaleString()}`);
      if (form.photo) fd.append('photo', form.photo);
      if (form.passport) fd.append('passport', form.passport);

      const resp = await axios.post(`${BACKEND_URL}/api/business-applications`, fd);
      const appNumber = resp?.data?.applicationNumber || resp?.data?.bookingNumber || '';
      const looksLikeTelegram = /^@/.test(contactValue) || /telegram/i.test(form.mobile || '');
      if (looksLikeTelegram && appNumber) {
        // Stash the deep link so the success view can render a "Open Telegram" CTA
        setTelegramDeepLink(`https://t.me/Visadubaibot?start=${appNumber}`);
      }
      setSuccess(true);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const ZoneIcon = meta.icon;
  const currentStepMeta = STEP_META[step - 1];
  const StepIcon = currentStepMeta.Icon;

  const modal = (
    <div className="bbm-overlay" onClick={(e) => e.target.classList.contains('bbm-overlay') && onClose()} data-testid="business-booking-modal">
      <div className="bbm-modal">
        <button className="bbm-close" onClick={onClose} aria-label="Close" data-testid="bbm-close">
          <X size={22} />
        </button>

        {/* Header */}
        <div className="bbm-header">
          <div className="bbm-header-icon"><ZoneIcon size={22} /></div>
          <div>
            <h2 className="bbm-title">{meta.name}</h2>
            <p className="bbm-subtitle">AED {totalPrice.toLocaleString()} · {zone === 'freezone' ? '7-14 Days' : '10-21 Days'}</p>
          </div>
        </div>

        {success ? (
          <div className="bbm-success">
            <div className="bbm-success-icon"><Check size={48} /></div>
            <h3>{t('applicationSubmitted') || 'Application Submitted!'}</h3>
            <p>{t('businessSuccessText') || 'We received your request. Our team will reach out within 24 hours.'}</p>
            
            {telegramDeepLink && (
              <a
                href={telegramDeepLink}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="bbm-success-telegram-cta"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '10px',
                  marginTop: '18px',
                  padding: '14px 28px',
                  background: 'linear-gradient(135deg, #229ED9, #1B7DBA)',
                  color: '#fff',
                  textDecoration: 'none',
                  borderRadius: '10px',
                  fontWeight: 600,
                  fontSize: '15px',
                  boxShadow: '0 6px 20px rgba(34, 158, 217, 0.35)',
                }}
              >
                <Send size={18} />
                {t('openTelegramBot') || 'Open @Visadubaibot to receive documents'}
              </a>
            )}
            
            <button className="bbm-btn bbm-btn-primary" onClick={onClose} data-testid="bbm-success-close" style={{ marginTop: telegramDeepLink ? '12px' : '0' }}>
              {t('close') || 'Close'}
            </button>
          </div>
        ) : (
          <>
            {/* Step indicator (8 pills) */}
            <div className="bbm-steps">
              {STEP_META.map((s) => (
                <div
                  key={s.num}
                  className={`bbm-step ${s.num === step ? 'is-active' : ''} ${s.num < step ? 'is-done' : ''}`}
                  data-testid={`bbm-step-${s.num}`}
                >
                  <span>{s.num < step ? <Check size={12} /> : s.num}</span>
                </div>
              ))}
            </div>

            {/* Step content */}
            <div className="bbm-content" key={step}>
              <div className="bbm-step-heading">
                <div className="bbm-step-heading-icon"><StepIcon size={20} /></div>
                <div>
                  <h3>{currentStepMeta.title}</h3>
                </div>
              </div>

              {step === 1 && (
                <div className="bbm-field">
                  <label>{t('bbmCompanyName') || 'Company Name'} *</label>
                  <input
                    type="text"
                    autoFocus
                    value={form.companyName}
                    onChange={(e) => update('companyName', e.target.value)}
                    data-testid="bbm-company-name"
                  />
                </div>
              )}

              {step === 2 && (
                <div className="bbm-row">
                  <div className="bbm-field">
                    <label>{t('firstName') || 'First Name'} *</label>
                    <input type="text" autoFocus value={form.firstName} onChange={(e) => update('firstName', e.target.value)} data-testid="bbm-first-name" />
                  </div>
                  <div className="bbm-field">
                    <label>{t('lastName') || 'Last Name'} *</label>
                    <input type="text" value={form.lastName} onChange={(e) => update('lastName', e.target.value)} data-testid="bbm-last-name" />
                  </div>
                </div>
              )}

              {step === 3 && (
                <div className="bbm-field">
                  <label>{t('nationality') || 'Nationality'} *</label>
                  <input type="text" autoFocus value={form.nationality} onChange={(e) => update('nationality', e.target.value)} placeholder="e.g. Uzbekistan" data-testid="bbm-nationality" />
                  <small>{t('bbmNationalityHint') || 'As shown on your passport'}</small>
                </div>
              )}

              {step === 4 && (
                <div className="bbm-field">
                  <label>{t('phoneNumber') || 'Phone Number'} *</label>
                  <input type="tel" autoFocus value={form.mobile} onChange={(e) => update('mobile', e.target.value)} placeholder="+971 50 123 4567" data-testid="bbm-mobile" />
                  
                  {/* Same-number toggle */}
                  <label
                    className="bbm-same-toggle"
                    data-testid="bbm-same-as-phone-toggle"
                  >
                    <input
                      type="checkbox"
                      checked={!!form.sameAsPhone}
                      onChange={(e) => setForm((p) => ({ ...p, sameAsPhone: e.target.checked, telegram: e.target.checked ? '' : p.telegram }))}
                    />
                    <Send size={13} className="bbm-same-toggle-icon" />
                    {t('sameNumberForTelegram') || 'I use the same number for Telegram / WhatsApp'}
                  </label>
                  
                  {/* Telegram/WhatsApp — hidden when sameAsPhone is checked */}
                  {!form.sameAsPhone && (
                    <div style={{ marginTop: '14px' }} data-testid="bbm-telegram-wrapper">
                      <label style={{ color: '#229ED9' }}>
                        {t('telegramOrWhatsapp') || 'Telegram or WhatsApp Number'} *
                      </label>
                      <input
                        type="text"
                        value={form.telegram}
                        onChange={(e) => update('telegram', e.target.value)}
                        placeholder="@username or +971 50 123 4567"
                        data-testid="bbm-telegram"
                        style={{ borderColor: 'rgba(34, 158, 217, 0.35)' }}
                      />
                      <small>{t('telegramShareDocsHint') || 'We will share your approved documents to this Telegram/WhatsApp.'}</small>
                    </div>
                  )}
                </div>
              )}

              {step === 5 && (
                <div className="bbm-field">
                  <label>{t('email') || 'Email Address'} *</label>
                  <input type="email" autoFocus value={form.email} onChange={(e) => update('email', e.target.value)} placeholder="you@example.com" data-testid="bbm-email" />
                  <small>{t('bbmEmailHint') || "We'll send your application confirmation here"}</small>
                </div>
              )}

              {step === 6 && (
                <>
                  <p className="bbm-note">{t('bbmPhotoNote') || 'Upload a clear, recent personal photo (passport-style). Optional — you can send it later.'}</p>
                  <button type="button" className="bbm-upload-btn bbm-upload-full" onClick={() => photoRef.current?.click()} data-testid="bbm-upload-photo">
                    {form.photoPreview ? (
                      <>
                        <img src={form.photoPreview} alt="photo" />
                        <span className="bbm-upload-change">{t('changePhoto') || 'Change'}</span>
                      </>
                    ) : (
                      <>
                        <Upload size={28} />
                        <span>{t('uploadPhoto') || 'Click to upload photo'}</span>
                        <small>{t('photoFormat') || 'JPG or PNG, max 5MB'}</small>
                      </>
                    )}
                  </button>
                  <input ref={photoRef} type="file" accept="image/*" hidden onChange={handleFile('photo', 'photoPreview')} />
                </>
              )}

              {step === 7 && (
                <>
                  <p className="bbm-note">{t('bbmPassportNote') || 'Upload a clear scan/photo of your passport data page. Optional — you can send it later.'}</p>
                  <button type="button" className="bbm-upload-btn bbm-upload-full" onClick={() => passportRef.current?.click()} data-testid="bbm-upload-passport">
                    {form.passportPreview ? (
                      <>
                        <img src={form.passportPreview} alt="passport" />
                        <span className="bbm-upload-change">{t('changeFile') || 'Change'}</span>
                      </>
                    ) : (
                      <>
                        <Upload size={28} />
                        <span>{t('uploadPassport') || 'Click to upload passport'}</span>
                        <small>{t('passportFormat') || 'JPG, PNG or PDF, max 5MB'}</small>
                      </>
                    )}
                  </button>
                  <input ref={passportRef} type="file" accept="image/*,.pdf" hidden onChange={handleFile('passport', 'passportPreview')} />
                </>
              )}

              {step === 8 && (
                <div className="bbm-review">
                  <label className="bbm-addon">
                    <input
                      type="checkbox"
                      checked={form.wantVisa}
                      onChange={(e) => update('wantVisa', e.target.checked)}
                      data-testid="bbm-want-visa"
                    />
                    <span>
                      {t('bbmWantVisa') === 'bbmWantVisa' ? 'Include Investor Visa' : t('bbmWantVisa')}{' '}
                      <strong>(+AED {visaAddOnPrice.toLocaleString()})</strong>
                    </span>
                  </label>

                  {/* Investor Visa preview — shown when user includes a visa */}
                  {form.wantVisa && (
                    <div className="bbm-visa-card" data-testid="bbm-visa-preview">
                      <div className="bbm-visa-card-bg">
                        <div className="bbm-visa-flag" />
                        <div className="bbm-visa-pattern" />
                      </div>
                      <div className="bbm-visa-card-inner">
                        <div className="bbm-visa-card-head">
                          <div className="bbm-visa-card-country">
                            <span className="bbm-visa-card-emblem">
                              <Shield size={18} />
                            </span>
                            <div>
                              <div className="bbm-visa-card-country-name">UNITED ARAB EMIRATES</div>
                              <div className="bbm-visa-card-country-sub">الإمارات العربية المتحدة</div>
                            </div>
                          </div>
                          <div className="bbm-visa-card-type">INVESTOR VISA</div>
                        </div>

                        <div className="bbm-visa-card-body">
                          <div className="bbm-visa-card-photo">
                            {form.photoPreview ? (
                              <img src={form.photoPreview} alt="holder" />
                            ) : (
                              <User size={36} />
                            )}
                          </div>
                          <div className="bbm-visa-card-fields">
                            <div className="bbm-visa-field">
                              <span className="bbm-visa-label">NAME / الاسم</span>
                              <span className="bbm-visa-value">
                                {(`${form.firstName} ${form.lastName}`.trim()) || '—'}
                              </span>
                            </div>
                            <div className="bbm-visa-field-row">
                              <div className="bbm-visa-field">
                                <span className="bbm-visa-label">NATIONALITY</span>
                                <span className="bbm-visa-value">{form.nationality || '—'}</span>
                              </div>
                              <div className="bbm-visa-field">
                                <span className="bbm-visa-label">VALIDITY</span>
                                <span className="bbm-visa-value bbm-visa-accent">2 YEARS</span>
                              </div>
                            </div>
                            <div className="bbm-visa-field-row">
                              <div className="bbm-visa-field">
                                <span className="bbm-visa-label">ISSUE DATE</span>
                                <span className="bbm-visa-value">{formatDate(issueDate)}</span>
                              </div>
                              <div className="bbm-visa-field">
                                <span className="bbm-visa-label">EXPIRY DATE</span>
                                <span className="bbm-visa-value">{formatDate(expiryDate)}</span>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="bbm-visa-card-foot">
                          <div className="bbm-visa-mrz">
                            P&lt;UAE{(form.lastName || '').toUpperCase().replace(/\s+/g, '&lt;')}&lt;&lt;{(form.firstName || '').toUpperCase().replace(/\s+/g, '&lt;')}&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;
                          </div>
                          <div className="bbm-visa-stamp">APPROVED</div>
                        </div>
                      </div>
                    </div>
                  )}

                  <dl>
                    <div><dt>{t('bbmCompanyName') || 'Company'}</dt><dd>{form.companyName}</dd></div>
                    <div><dt>{t('fullName') || 'Full Name'}</dt><dd>{form.firstName} {form.lastName}</dd></div>
                    <div><dt>{t('nationality') || 'Nationality'}</dt><dd>{form.nationality}</dd></div>
                    <div><dt>{t('phoneNumber') || 'Phone'}</dt><dd>{form.mobile}</dd></div>
                    {!form.sameAsPhone && form.telegram && (
                      <div><dt>{t('telegramOrWhatsapp') || 'Telegram / WhatsApp'}</dt><dd style={{ color: '#229ED9' }}>{form.telegram}</dd></div>
                    )}
                    <div><dt>{t('email') || 'Email'}</dt><dd className="bbm-dd-email">{form.email}</dd></div>
                    <div><dt>{t('zone') || 'Type'}</dt><dd>{meta.name}</dd></div>
                    <div><dt>{t('personalPhoto') || 'Photo'}</dt><dd>{form.photo ? '✓' : '—'}</dd></div>
                    <div><dt>{t('passportCopy') || 'Passport'}</dt><dd>{form.passport ? '✓' : '—'}</dd></div>
                    <div><dt>{t('investorVisa') || 'Investor Visa'}</dt><dd>{form.wantVisa ? (t('yes') || 'Yes') : (t('no') || 'No')}</dd></div>
                    <div className="bbm-total"><dt>{t('total') || 'Total'}</dt><dd><strong>AED {totalPrice.toLocaleString()}</strong></dd></div>
                  </dl>
                  {error && <div className="bbm-error">{error}</div>}
                </div>
              )}
            </div>

            {/* Footer actions */}
            <div className="bbm-footer">
              <button className="bbm-btn bbm-btn-ghost" onClick={step === 1 ? onClose : back} disabled={submitting} data-testid="bbm-back">
                <ChevronLeft size={16} /> {step === 1 ? (t('cancel') || 'Cancel') : (t('back') || 'Back')}
              </button>
              {step < TOTAL_STEPS ? (
                <button className="bbm-btn bbm-btn-primary" disabled={!canProceed()} onClick={next} data-testid="bbm-next">
                  {t('next') || 'Next'} <ChevronRight size={16} />
                </button>
              ) : (
                <button className="bbm-btn bbm-btn-primary" disabled={submitting} onClick={submit} data-testid="bbm-submit">
                  {submitting ? (t('submitting') || 'Submitting...') : (t('submitApplication') || 'Submit Application')}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
};

export default BusinessBookingModal;
